import moment from "moment";
import { Subject } from 'rxjs';

import UniqueIdGenerator from "../utils/uniqueIdGenerator.js";
import InternalEventBus from "../utils/internalEventBus";
import ExpressionEvaluator from "../utils/expressionEvaluator";
import DashboardService from "../services/dashboardService";
import TemplateParser from "../utils/templateParser";
import { variableTypes } from "../../components/dashboard/control-widget/selection-contorl/constants";
import { findOptimalResolution, getResolutionPeriod, getResolutionPeriodByStartTime } from "../utils/resolution";

window.functionBufferSize = 50;

export default class Widget {
  constructor(newId, widgetType, widgetTitle, legends, belongingComponent, metaData = {}, autoAdjustWindowSize, centerZeroLine, splitLeftRight) {
    this.id = newId;
    this.type = widgetType;
    this.components = {};
    this.legends = legends || [];
    this.title = widgetTitle || "";
    this.autoAdjustWindowSize = autoAdjustWindowSize || false;
    this.centerZeroLine = centerZeroLine || false;
    this.splitLeftRight = splitLeftRight || false;
    this.belongingComponent = belongingComponent;
    this.registeredDatasets = {};
    this.areHistoricDataLoaded = false;
    this.componentHistoricDataLoadTracker = {};
    this.metaData = metaData;
    this.legends.forEach((legend, index) => {
      this.generateEvaluationFunctions(legend, index);
    });
    this.setDefaultMetaData();
    this.evaluateMetaData();
    this.enableResolutionWithServerSideFunctions();
  }

  setDefaultMetaData() {
    if (this.metaData.maxRepeatDuration == null) {
      this.metaData.maxRepeatDuration = 1;
    }
  }

  addLegends(newLegends) {
    newLegends.map(legend => {
      let doesExist = false;
      for (let i = 0; i < this.legends.length; i++) {
        if (this.legends[i].id === legend.id) {
          doesExist = true;
          break;
        }
      }

      if (!doesExist) {
        this.generateEvaluationFunctions(legend);
        this.legends.push(legend);
      }

      this.subscribeDatasetUpdateEvent(legend);
    });
  }

  removeLegends(legendsToDelete) {
    // delete legends
    var deletedDataStreams = [];
    for (var i in legendsToDelete) {
      var legendToDelete = legendsToDelete[i];
      for (var j in this.legends) {
        var targetDataField = this.legends[j];

        if (targetDataField.id === legendToDelete.id) {

          if (!doesExistInDeletedStreams(deletedDataStreams, targetDataField.datasetId)) {
            deletedDataStreams.push(targetDataField.datasetId);
          }
          this.legends.splice(j, 1);
          break;
        }
      }
    }

    // find obsolete data stream
    var obsoleteDataStreams = [];
    for (var i in deletedDataStreams) {
      if (!doesWidgetStillRequire(this.legends, deletedDataStreams[i])) {
        obsoleteDataStreams.push(deletedDataStreams[i]);
      }
    }

    // notify components
    for (var index in this.components) {
      if (this.components[index].controller.doesNotHaveAnyLegends()) {
        delete this.components[index];
      }
    }

    this.unsubscribeEvents(obsoleteDataStreams);

    return obsoleteDataStreams;

    // inner functions
    function doesExistInDeletedStreams(deletedDataStreams, datasetId) {
      var doesExist = false;
      for (var i in deletedDataStreams) {
        if (deletedDataStreams[i] === datasetId) {
          doesExist = true;
          break;
        }
      }

      return doesExist;
    }

    function doesWidgetStillRequire(legends, datasetId) {
      for (var i in legends) {
        if (legends[i].datasetId === datasetId) {
          return true;
        }
      }

      return false;
    }
  }


  unsubscribeEvents(datasetIds) {
    datasetIds.map(datasetId => {
      InternalEventBus.unsubscribe(this.registeredDatasets[datasetId]);
      delete this.registeredDatasets[datasetId];
    });
  }

  destroy() {
    let datasetIds = Object.keys(this.registeredDatasets);
    this.unsubscribeEvents(datasetIds);
  }

  addComponent(componentController, parameters) {
    this.componentHistoricDataLoadTracker[componentController.id] = false;

    this.components[componentController.id] = {
      controller: componentController,
      parameters: parameters
    };
  }

  resetComponents(componentId) {
    // delete components
    for (var i in this.components) {
      delete this.components[i];
    }
  };

  clone(widget) {
    const data = JSON.parse(JSON.stringify(this));
    const copy = new Widget(data.id,
      data.type,
      data.title,
      data.legends,
      data.belongingComponent,
      data.metaData,
      data.autoAdjustWindowSize,
      data.centerZeroLine,
      data.splitLeftRight);
    copy.legends = data.legends;

    return copy;
  };

  handleNewData(datasetId) {
    for (var index in this.components) {
      var componentController = this.components[index].controller;
      componentController.update(datasetId);
    }
  };

  subscribeDatasetUpdateEvent(legend) {
    for (let key in legend.mappings) {
      let datasetId = legend.mappings[key].datasetId;
      if (!this.registeredDatasets.hasOwnProperty(datasetId)) {
        let subscriptionToken = InternalEventBus.subscribe(datasetId, (data) => {
          if (this.areHistoricDataLoaded) {
            this.handleNewData(data.dataset);
          }
        });
        this.registeredDatasets[datasetId] = subscriptionToken;
      }
    }
  }


  updateLegends(legends) {
    for (var i = 0; i < legends.length; i++) {
      for (var j = 0; j < this.legends.length; j++) {
        if (this.legends[j].id === legends[i].id) {
          this.generateEvaluationFunctions(legends[i]);
          this.legends[j] = legends[i];
          this.subscribeDatasetUpdateEvent(legends[i]);
        }
      }
    }

    for (var index in this.components) {
      var componentController = this.components[index].controller;
      if (componentController.onLegendUpdated) {
        componentController.onLegendUpdated();
      }
    }
  }

  updateMetaData(metaData) {
    for (let key in metaData) {
      this.metaData[key] = metaData[key];
    }
  }

  evaluateMetaData() {
    const dashboardVariables = DashboardService.getCurrentDashboardVariables();
    Object.keys(this.metaData).forEach(key => {
      const meta = this.metaData[key];
      if (meta && typeof meta === "object" && meta.type === variableTypes.EXPRESSION) {
        const parsed = TemplateParser.parseTemplate(meta.expression, false, dashboardVariables);
        try {
          meta.value = eval(parsed);
        } catch (e) {
        }
      }
    });
  }

  generateEvaluationFunctions(legend, index) {
    const dashboardVariables = DashboardService.getCurrentDashboardVariables();
    legend["functions"] = {};
    legend.errors = {};
    let currentKey = 'y';

    if (legend.expressions.y && legend.expressions.y.length > 0) {
      this.evaluateDataSourceFunction(legend);
      const expressionWithoutSpace = legend.expressions.y.trim();
      try {
        const jsepObject = jsep(expressionWithoutSpace);
        const result = ExpressionEvaluator
          .generateSeverSideFunctionMap(jsepObject, dashboardVariables, legend.mappings);

        if (result.serverSideFunctionObjects.length > 0 && result.expression) {
          legend.serverSideFunction = result.serverSideFunctionObjects;
          legend.serverSideFunctionExpression = result.expression;
          legend.functions.y = ExpressionEvaluator.generateEvaluationFunction(legend.serverSideFunctionExpression, legend.mappings, dashboardVariables);

          // support multiple fields ex) bubble
          for (let key in legend.expressions) {
            if (key !== 'y') {
              currentKey = key;
              const expression = legend.serverSideFunctionExpression.split('.')[0];
              legend.functions[key] =
                ExpressionEvaluator.generateEvaluationFunction(
                  `${expression}.${legend.expressions[key]}`, legend.mappings, dashboardVariables);
            }
          }
        } else {
          legend.serverSideFunction = [];
          legend.serverSideFunctionExpression = "";
          for (let key in legend.expressions) {
            const expression = legend.expressions[key];
            currentKey = key;
            if (expression.trim().length) {
              let mappings = JSON.parse(JSON.stringify(legend.mappings));
              let splitExpression = true;
              let anyMappingMatch = false;
              Object.keys(mappings).forEach(key => {
                if (expression.indexOf(key) > -1) {
                  anyMappingMatch = true;
                }
              });
              if (!anyMappingMatch && key !== 'y') {
                // secondary expressions without mappings ex) qty, filter, size from bubble
                const { datasetId } = mappings[legend.expressions.y];
                mappings[expression] = {
                  datasetId,
                  fieldId: expression,
                  value: expression
                };
                splitExpression = false;
              }
              legend.functions[key] =
                ExpressionEvaluator.generateEvaluationFunction(expression, mappings, dashboardVariables, splitExpression);
            }
          }
        }
      } catch (e) {
        console.log(`[Widget] Generating evaluation function error`);
        console.log(`Expression: ${legend.expressions.y}`);
        console.log(`Error: ${e.message}`);
        legend.errors[currentKey] = e.message;
      }
    }
  }

  evaluateDataSourceFunction(legend) {
    // Remove all dataSource mappings first
    Object.keys(legend.mappings).forEach(key => {
      if (legend.mappings[key].value.indexOf('DataSource(') > -1 ) {
        delete legend.mappings[key]
      }
    });

    // legend.expressions.y = DataSource(x, y) + DataSource(x1, y1)
    if (legend.DataSource == null) return;
    const variables = DashboardService.getCurrentDashboardVariables();
    const dataSourceFunctions = legend.DataSource.match(/DataSource\(([^()]+)\)/g);  // 0: DataSource(x, y), 1: DataSource(x1, y1)
    if (dataSourceFunctions) {
      let y = legend.DataSource;
      Object.keys(dataSourceFunctions).forEach(key => {
        const mappingKey = UniqueIdGenerator.id();  // xyz
        let dsf = dataSourceFunctions[key].trim();
        const matches = dsf.match(/\(([^)]+)\)/);   // (x,y)
        y = y.replace(dsf, mappingKey).trim();  // legend.expressions.y = xyz + DataSource(x1, y1)
        if (matches[1]) {
          const params = matches[1].replace(/['"]/g, '').split(",");
          const p0 = params[0].trim();
          const p1 = params[1].trim();
          const mapping = {
            value: dsf
          };

          if (variables[p0]) {
            const { value, name } = variables[p0];
            mapping.datasetId = value;

            mapping.value = mapping.value.replace(new RegExp(p0, 'g'), name);
          } else {
            mapping.datasetId = p0.trim();
          }

          if (variables[p1]) {
            const { value, name } = variables[p1];
            mapping.fieldId = value;
            mapping.value = mapping.value.replace(new RegExp(p1, 'g'), name);
          } else {
            mapping.fieldId = p1.trim();
          }

          legend.expressions.y = y;
          legend.mappings[mappingKey] = mapping;
        }
      });
    }
  }

  onComponentSnapshotObtained(componentId, result, noOfRecords) {
    this.componentHistoricDataLoadTracker[componentId] = true;

    // update the areHistoricDataLoaded flag
    this.areHistoricDataLoaded = true;
    for (var component_id in this.componentHistoricDataLoadTracker) {
      if (!this.componentHistoricDataLoadTracker[component_id]) {
        this.areHistoricDataLoaded = false;
        break;
      }
    }

    // load the snapshot data using controller
    for (var targetComponentId in this.components) {
      if (targetComponentId === componentId) {
        var componentController = this.components[targetComponentId].controller;
        if (result.hasOwnProperty("data")) {
          // resolution
          componentController.loadHistoricData(result.data, noOfRecords, result.eventData, result.restored);
        } else {
          // tick
          componentController.loadHistoricData(result, noOfRecords, [], result.restored);
        }
        break;
      }
    }
  }

  enableResolutionWithServerSideFunctions() {
    const resolution = this.metaData.resolution;
    let autoResolution;

    if (resolution && resolution[0] === "auto") {
      const startAndEndTime = this.findStartAndEndTimeFromFunctions();
      autoResolution = findOptimalResolution(startAndEndTime.start, startAndEndTime.end);
      this.autoAdjustWindowSize = true;
    }

    this.legends.forEach(l => {
      if (l.serverSideFunction) {
        l.serverSideFunction.forEach(sf => {
          const p = sf.params;

          if (!resolution || (resolution && resolution[0] === "tick")) {
            // tick mode
            if ((sf.name === "TSCalculate" && p[6] === 7) || sf.name === "GetDataForTimeRange") {
              sf.name = "GetDataAfterTimeForResolution";
              p[3] = this.metaData.historySize + 2;  // number of data
            }
          } else if (resolution && resolution[0] === "auto") {
            // auto mode
            if (sf.name === "TSCalculate" && p[6] === 7) {
              p[4] = autoResolution[3];
              p[5] = autoResolution[2];
            }
          } else {
            // resolution mode
            const resolutionPeriod = getResolutionPeriodByStartTime(p[2], resolution[1], this.metaData.historySize);
            if (sf.name === "TSCalculate" && p[6] === 7) {
              p[2] = resolutionPeriod.start;
              p[3] = resolutionPeriod.end;
              p[4] = resolution[3];
              p[5] = resolution[2];
            } else if (sf.name === "GetDataForTimeRange") {
              p[2] = resolutionPeriod.start;
              p[3] = resolutionPeriod.end;
            }
          }
        });
      }
    });
  }

  findStartAndEndTimeFromFunctions() {
    let allFunctions = [];
    let minStartTime = null;
    let maxEndTime = null;
    this.legends.forEach(l => {
      if (l.serverSideFunction) {
        allFunctions = [...allFunctions, ...l.serverSideFunction];
      }
    });

    allFunctions.forEach(f => {
      const startTime = new Date(f.params[2]).getTime();
      const endTime = new Date(f.params[3]).getTime();

      if (!isNaN(startTime)) {
        if (minStartTime === null || minStartTime > startTime) {
          minStartTime = startTime;
        }
      }

      if (!isNaN(endTime)) {
        if (maxEndTime === null || maxEndTime < endTime) {
          maxEndTime = endTime;
        }
      }
    });

    return {
      start: minStartTime,
      end: maxEndTime
    }
  }

  static getFormattedDateTimeString(metaData, timeInMillisecond, showMillisecond = false) {
    const defaultFormat = "MMM D YYYY HH:mm:ss";
    let format = TemplateParser.parseTemplate(metaData.timeTickFormat || defaultFormat);
    if (format === defaultFormat && showMillisecond) {
      format += ".SSS";
    }
    const m = moment(timeInMillisecond);
    if (metaData.timeAdjustmentOption && metaData.timeAdjustmentOptionValue) {
      try {
        const adjustValue = parseInt(metaData.timeAdjustmentOptionValue);
        m.add(adjustValue, metaData.timeAdjustmentOption);
      } catch (e) {

      }
    }
    return m.format(format);
  }
}


// WEBPACK FOOTER //
// ./src/core/models/widget.js